import { Link } from "@tanstack/react-router";
import { Flame, MousePointerClick, Users, Globe, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatHeat, outboundUrl } from "@/lib/behot";
import { achievementKey, type HistoryEntry } from "@/lib/history";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";

function Avatar({ entry }: { entry: HistoryEntry }) {
  if (entry.image_url) {
    return (
      <img
        src={entry.image_url}
        alt={entry.listing_name}
        width={48}
        height={48}
        className="size-12 shrink-0 rounded-xl object-cover"
        loading="lazy"
        decoding="async"
      />
    );
  }
  return (
    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent text-lg font-bold text-accent-foreground">
      {entry.listing_name.charAt(0).toUpperCase()}
    </div>
  );
}

/** One frozen ranking row. Mirrors the live board entry layout. */
export function HistoryEntryRow({ entry }: { entry: HistoryEntry }) {
  const { t } = useT();
  const badge = achievementKey(entry.position);

  return (
    <li
      className={cn(
        "card-surface p-3 sm:p-4",
        entry.position === 1 && "border-primary/50 bg-primary/5",
      )}
    >
      <div className="flex items-start gap-3 sm:items-center sm:gap-4">
        <div className="w-8 shrink-0 text-center sm:w-14">
          <div className="rank-numeral text-xl leading-none sm:text-3xl">
            {String(entry.position).padStart(2, "0")}
          </div>
        </div>
        <Avatar entry={entry} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-base font-semibold sm:text-lg">
            <Link
              to="/p/$slug"
              params={{ slug: entry.listing_slug }}
              className="hover:text-primary"
            >
              {entry.listing_name}
            </Link>
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {badge && (
              <Badge variant={entry.position === 1 ? "default" : "secondary"}>
                {t(`history.badge.${badge === "one" ? "one" : badge}`)}
              </Badge>
            )}
            {entry.owner_path && entry.owner_name && (
              <Link
                to="/user/$slug"
                params={{ slug: entry.owner_slug ?? "" }}
                search={{ ref: undefined }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {entry.owner_name}
              </Link>
            )}
            {entry.country_code && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="size-3" />
                {entry.country_code}
              </span>
            )}
          </div>
          {(entry.listing_headline ?? entry.listing_description) && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {entry.listing_headline ?? entry.listing_description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-semibold text-primary">
              <Flame className="size-3.5" />
              {formatHeat(entry.heat_cents)} {t("history.heat")}
            </span>
            {entry.booster_count > 0 && (
              <span className="inline-flex items-center gap-1">
                <Users className="size-3.5" />
                {t("history.supporters", { n: entry.booster_count })}
              </span>
            )}
            {entry.click_count > 0 && (
              <span className="inline-flex items-center gap-1">
                <MousePointerClick className="size-3.5" />
                {t("history.clicks", { n: entry.click_count })}
              </span>
            )}
            {entry.external_url && (
              <a
                href={outboundUrl(entry.external_url)}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <Globe className="size-3.5" />
                {t("history.website")}
              </a>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
